import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import "../Module.scss";
import {
  useXlsxProcessorQuery,
  useLazyXlsxProcessorQuery,
} from "../../../services/serverless";
import { Error, tryAgainLaterMessage } from "../../../components/Error";
import { CelinkFinModelInputs } from "./FinancialModelCelinkInputs";
import {
  CelinkFinancialModelingKPI,
  divideOutputsIntoChartGroupDataArray,
  HeadcountAndNoTitleTable,
  OtherTable,
  PLExHudAndPLHuDTable,
  PortfolioTable,
  WorkingCapitalTable,
} from "./CelinkFinModelHelperFunctions";
import {
  ParentChildInterface,
  InputSliderTableInterface,
} from "../../../services/types/gallus";
import { fillInputWithValues } from "../../../components/generic-financial-model/HelperFunctions";
import { FinancialModelCelinkOutputsHelper } from "./FinancialModelCelinkOutputs";
import Select, { MultiValue, SingleValue } from "react-select";
import { ReactSelectOption } from "../../../features/Interfaces";
import { defaultInputArrayValues } from "./CelinkInputsArray";
import { faArrowUp, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import Plot from "react-plotly.js";
import { Link } from "react-router-dom";
import {
  bsKPIGroups,
  DataSourceURL,
  EmptySelection,
  inputGroupNames,
  inputGroups,
  kpiRowValues,
  plKPIGroups,
  dataLocation,
  yearGroups,
  kpiMetadata,
  chartGroupData,
  xAxis,
} from "./CelinkFinancialModelStaticData";

interface CelinkFinancialModelingProps {}

interface CelinkFinancialModelingState {
  //Checks if initial input data is loaded or not
  initialDataLoadedFlag: boolean;
  //Flag that, if true, signifies that the charts have been loaded
  chartsLoadedFlag: boolean;
  //Flag that, if true, signifies that the charts are loadeding
  chartsLoadingFlag: boolean;
  //State for the selectors of Input and Year. Will be stored, it's values added to inputArray, and then
  //replaced by empty values.
  selectedInputTMP: ReactSelectOption;
  selectedYearTMP: ReactSelectOption;
  //Array that contains all the inputs (name and year). It also contains a flag that makes it known
  //if the input was selected or not.
  inputsArray: InputSliderTableInterface[];
  //State that controls if the "Add Inputs" button is disabled or not
  addInputsDisableFlag: boolean;
  //State that controls if the "Inputs" menu is open or not
  openInputsMenu: boolean;
  //States containing the data to be used and changed. Each state is an array (representing all values for that year)
  //inside another array (which is a collection of all years, from 2024 to 2029).
  PLExHud: (number | null)[][];
  PLHud: (number | null)[][];
  Headcount: (number | null)[][];
  NoTitle: (number | null)[][];
  Portfolio: (number | null)[][];
  WorkingCapital: (number | null)[][];
  Other: (number | null)[][];
  //States that contain the initial values of the previous states. To be used in handleClose function.
  PLExHudInitialValues: (number | null)[][];
  PLHudInitialValues: (number | null)[][];
  HeadcountInitialValues: (number | null)[][];
  NoTitleInitialValues: (number | null)[][];
  PortfolioInitialValues: (number | null)[][];
  WorkingCapitalInitialValues: (number | null)[][];
  OtherInitialValues: (number | null)[][];
  //The selected KPIs. These are the selected chart groups.
  SelectedPLKpis: ReactSelectOption[];
  SelectedBSKpis: ReactSelectOption[];
  //The data for the charts. An array of all the KPI groups (P&L plus BS KPIs). Each member of that array is
  //an array of chart data. Each of their members is an array of numbers, the index being the year minus 2024.
  chartsData: chartGroupData[];
  errorFlag: boolean;
  errorCode: string;
  errorMessage: string;
  errorType: string;
}

export const CelinkFinancialModeling = (
  props: CelinkFinancialModelingProps,
) => {
  const [state, setState] = useState<CelinkFinancialModelingState>({
    initialDataLoadedFlag: false,
    chartsLoadingFlag: false,
    selectedInputTMP: EmptySelection,
    selectedYearTMP: EmptySelection,
    inputsArray: defaultInputArrayValues,
    addInputsDisableFlag: false,
    chartsLoadedFlag: false,
    chartsData: [],
    openInputsMenu: true,
    PLExHud: [],
    PLHud: [],
    Headcount: [],
    NoTitle: [],
    Portfolio: [],
    WorkingCapital: [],
    Other: [],
    PLExHudInitialValues: [],
    PLHudInitialValues: [],
    HeadcountInitialValues: [],
    NoTitleInitialValues: [],
    PortfolioInitialValues: [],
    WorkingCapitalInitialValues: [],
    OtherInitialValues: [],
    SelectedBSKpis: [],
    SelectedPLKpis: [],
    errorFlag: false,
    errorCode: "",
    errorMessage: "",
    errorType: "",
  });

  const [chartDataQuery, chartDataResult] = useLazyXlsxProcessorQuery();

  const InitialChartData = useXlsxProcessorQuery({
    DataSource: DataSourceURL,
    InputSheetName: null,
    Inputs: [],
    OutputSheetName: "Inputs",
    Outputs: CelinkFinModelInputs,
  });

  //This hook takes the value array from the initial call and neatly spreads it's values
  //on each individual states. It's not strictly necessary but it improves readability.
  useEffect(() => {
    if (InitialChartData.isSuccess && InitialChartData.status === "fulfilled") {
      var valueArray: (number | null)[] = [];
      for (var i = 0; i < InitialChartData.data.Outputs.length; i++) {
        valueArray.push(InitialChartData.data.Outputs[i].Value);
      }

      setState({
        ...state,
        PLExHud: [
          valueArray.slice(0, 21),
          valueArray.slice(21, 42),
          valueArray.slice(42, 63),
          valueArray.slice(63, 84),
          valueArray.slice(84, 105),
          valueArray.slice(105, 126),
        ],
        PLHud: [
          valueArray.slice(126, 147),
          valueArray.slice(147, 168),
          valueArray.slice(168, 189),
          valueArray.slice(189, 210),
          valueArray.slice(210, 231),
          valueArray.slice(231, 252),
        ],
        Headcount: [
          valueArray.slice(252, 282),
          valueArray.slice(282, 312),
          valueArray.slice(312, 342),
          valueArray.slice(342, 372),
          valueArray.slice(372, 402),
          valueArray.slice(402, 432),
        ],
        NoTitle: [
          valueArray.slice(432, 462),
          valueArray.slice(462, 492),
          valueArray.slice(492, 522),
          valueArray.slice(522, 552),
          valueArray.slice(552, 582),
          valueArray.slice(582, 612),
        ],
        Portfolio: [
          valueArray.slice(612, 645),
          valueArray.slice(645, 678),
          valueArray.slice(678, 711),
          valueArray.slice(711, 744),
          valueArray.slice(744, 777),
          valueArray.slice(777, 810),
        ],
        WorkingCapital: [
          valueArray.slice(810, 816),
          valueArray.slice(816, 822),
          valueArray.slice(822, 828),
          valueArray.slice(828, 834),
          valueArray.slice(834, 840),
          valueArray.slice(840, 846),
        ],
        Other: [
          valueArray.slice(846, 851),
          valueArray.slice(851, 856),
          valueArray.slice(856, 861),
          valueArray.slice(861, 866),
          valueArray.slice(866, 871),
          valueArray.slice(871, 876),
        ],
        PLExHudInitialValues: [
          valueArray.slice(0, 21),
          valueArray.slice(21, 42),
          valueArray.slice(42, 63),
          valueArray.slice(63, 84),
          valueArray.slice(84, 105),
          valueArray.slice(105, 126),
        ],
        PLHudInitialValues: [
          valueArray.slice(126, 147),
          valueArray.slice(147, 168),
          valueArray.slice(168, 189),
          valueArray.slice(189, 210),
          valueArray.slice(210, 231),
          valueArray.slice(231, 252),
        ],
        HeadcountInitialValues: [
          valueArray.slice(252, 282),
          valueArray.slice(282, 312),
          valueArray.slice(312, 342),
          valueArray.slice(342, 372),
          valueArray.slice(372, 402),
          valueArray.slice(402, 432),
        ],
        NoTitleInitialValues: [
          valueArray.slice(432, 462),
          valueArray.slice(462, 492),
          valueArray.slice(492, 522),
          valueArray.slice(522, 552),
          valueArray.slice(552, 582),
          valueArray.slice(582, 612),
        ],
        PortfolioInitialValues: [
          valueArray.slice(612, 645),
          valueArray.slice(645, 678),
          valueArray.slice(678, 711),
          valueArray.slice(711, 744),
          valueArray.slice(744, 777),
          valueArray.slice(777, 810),
        ],
        WorkingCapitalInitialValues: [
          valueArray.slice(810, 816),
          valueArray.slice(816, 822),
          valueArray.slice(822, 828),
          valueArray.slice(828, 834),
          valueArray.slice(834, 840),
          valueArray.slice(840, 846),
        ],
        OtherInitialValues: [
          valueArray.slice(846, 851),
          valueArray.slice(851, 856),
          valueArray.slice(856, 861),
          valueArray.slice(861, 866),
          valueArray.slice(866, 871),
          valueArray.slice(871, 876),
        ],
        initialDataLoadedFlag: true,
        errorFlag: false,
      });
    } else if (InitialChartData.isError) {
      setState((prevState) => ({
        ...prevState,
        errorFlag: true,
        errorCode: "500",
        errorMessage: tryAgainLaterMessage,
        errorType: "InternalAPI",
      }));
    }
  }, [InitialChartData]);

  //This function handles input selector change.
  const handleInputSelectionChange = (
    selectedOption: SingleValue<ReactSelectOption>,
  ) => {
    if (selectedOption) {
      setState({ ...state, selectedInputTMP: selectedOption });
    }
  };

  //This function handles year selector change.
  const handleYearSelectionChange = (
    selectedOption: SingleValue<ReactSelectOption>,
  ) => {
    if (selectedOption) {
      setState({ ...state, selectedYearTMP: selectedOption });
    }
  };

  //This function handles year selector change.
  const handlePLKPISelectionChange = (
    selectedOptions: MultiValue<ReactSelectOption>,
  ) => {
    if (Array.isArray(selectedOptions)) {
      setState({ ...state, SelectedPLKpis: selectedOptions });
    }
  };

  //This function handles year selector change.
  const handleBSKPISelectionChange = (
    selectedOptions: MultiValue<ReactSelectOption>,
  ) => {
    if (Array.isArray(selectedOptions)) {
      setState({ ...state, SelectedBSKpis: selectedOptions });
    }
  };

  //This hook spreads the values of the resulting charts into the chartsData state.
  useEffect(() => {
    if (chartDataResult.isSuccess && chartDataResult.status === "fulfilled") {
      var rowsToSearch: kpiMetadata[] = [];

      [...state.SelectedPLKpis, ...state.SelectedBSKpis].map(
        (selectedKPI: ReactSelectOption) => {
          let searchTarget = kpiRowValues.find(
            (kpi) => kpi.kpiName === selectedKPI.value,
          );
          if (searchTarget) {
            rowsToSearch.push(searchTarget);
          }
        },
      );

      setState({
        ...state,
        chartsData: divideOutputsIntoChartGroupDataArray(
          chartDataResult.data.Outputs,
          rowsToSearch,
        ),
        chartsLoadingFlag: false,
        chartsLoadedFlag: true,
        errorFlag: false,
      });
    } else if (chartDataResult.isError) {
      setState((prevState) => ({
        ...prevState,
        errorFlag: true,
        errorCode: "500",
        errorMessage: tryAgainLaterMessage,
        errorType: "InternalAPI",
      }));
    }
  }, [chartDataResult]);

  //Sends the input data to the spreadsheet.
  async function sendDataBuildCharts() {
    var rowsToSearch: dataLocation[][] = [];
    [...state.SelectedPLKpis, ...state.SelectedBSKpis].map(
      (selectedKPI: ReactSelectOption) => {
        let searchTarget = kpiRowValues.find(
          (kpi) => kpi.kpiName === selectedKPI.value,
        );
        if (searchTarget) {
          rowsToSearch.push(searchTarget.rowData);
        }
      },
    );

    setState({ ...state, chartsLoadingFlag: true });
    chartDataQuery({
      DataSource: DataSourceURL,
      InputSheetName: "Inputs",
      Inputs: fillInputWithValues(state.inputsArray, [
        { inputData: state.PLExHud, inputName: inputGroupNames.PAndLExHUD },
        { inputData: state.PLHud, inputName: inputGroupNames.PAndLHUD },
        { inputData: state.Headcount, inputName: inputGroupNames.Headcount },
        { inputData: state.NoTitle, inputName: inputGroupNames.NoTitle },
        { inputData: state.Portfolio, inputName: inputGroupNames.Portfolio },
        {
          inputData: state.WorkingCapital,
          inputName: inputGroupNames.WorkingCapital,
        },
        { inputData: state.Other, inputName: inputGroupNames.Other },
      ]),
      OutputSheetName: "KPIs",
      Outputs: FinancialModelCelinkOutputsHelper(rowsToSearch, true),
    });
  }

  //This hook disables the "Add Input" button if the value to add is already being used, so the user
  //is not allowed to enter it twice.
  useEffect(() => {
    var newInput = {
      input: state.selectedInputTMP.value,
      year: Number(state.selectedYearTMP.value),
    };
    var containFlag = state.inputsArray.some((elem) => {
      return JSON.stringify(newInput) === JSON.stringify(elem);
    });
    if (!state.addInputsDisableFlag && containFlag) {
      setState({ ...state, addInputsDisableFlag: true });
    }

    if (state.addInputsDisableFlag && !containFlag) {
      setState({ ...state, addInputsDisableFlag: false });
    }
  }, [state.selectedInputTMP, state.selectedYearTMP, state.inputsArray]);

  //This function handles state changes in the child components. The index of the state
  //is a hardcoded value: 0 stands for state.PLExHud, 1 for state.PLHud, etc. The switch
  //case jumps values once, from 3 to 6, because Augie didn't want some inputs. I should change
  //the values to not have gaps.
  const handleChange = (newData: ParentChildInterface) => {
    var newArrayTMP: (number | null)[] = [];
    var newArrayOfArraysTMP: (number | null)[][] = [];
    switch (newData.indexOfState) {
      case 0:
        newArrayTMP = [...state.PLExHud[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.PLExHud];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          PLExHud: newArrayOfArraysTMP,
        });
        break;
      case 1:
        newArrayTMP = [...state.PLHud[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.PLHud];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          PLHud: newArrayOfArraysTMP,
        });
        break;
      case 2:
        newArrayTMP = [...state.Headcount[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.Headcount];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          Headcount: newArrayOfArraysTMP,
        });
        break;
      case 3:
        newArrayTMP = [...state.NoTitle[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.NoTitle];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          NoTitle: newArrayOfArraysTMP,
        });
        break;
      case 6:
        newArrayTMP = [...state.Portfolio[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.Portfolio];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          Portfolio: newArrayOfArraysTMP,
        });
        break;
      case 7:
        newArrayTMP = [...state.WorkingCapital[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.WorkingCapital];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          WorkingCapital: newArrayOfArraysTMP,
        });
        break;
      case 8:
        newArrayTMP = [...state.Other[newData.indexOfSubArray]];
        newArrayTMP[newData.indexOfChangedValue] = newData.value;
        newArrayOfArraysTMP = [...state.Other];
        newArrayOfArraysTMP[newData.indexOfSubArray] = newArrayTMP;
        setState({
          ...state,
          Other: newArrayOfArraysTMP,
        });
        break;
    }
  };

  //This functions handles closing the input group. On close, the state inputsArray is changed
  //so that the input group is hidden, and the changed value is returned to it's default value.
  const handleClose = (indexofSelectedInput: number, indexOfState: number) => {
    var newSelectedInputs = state.inputsArray;
    newSelectedInputs[indexofSelectedInput] = {
      enable: false,
      input: newSelectedInputs[indexofSelectedInput].input,
      year: newSelectedInputs[indexofSelectedInput].year,
    };
    var initialValues: any[] = [];
    switch (indexOfState) {
      case 0:
        state.PLExHudInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          PLExHud: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 1:
        state.PLHudInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          PLHud: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 2:
        state.HeadcountInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          Headcount: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 3:
        state.NoTitleInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          NoTitle: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 6:
        state.PortfolioInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          Portfolio: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 7:
        state.WorkingCapitalInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          WorkingCapital: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
      case 8:
        state.OtherInitialValues.forEach((values) => {
          initialValues.push(values);
        });
        setState({
          ...state,
          Other: initialValues,
          inputsArray: newSelectedInputs,
          addInputsDisableFlag: true,
        });
        break;
    }
  };

  const tableRenderingHandler = (
    input: string,
    year: number,
    inputsArrayIndex: number,
  ) => {
    switch (input) {
      case inputGroupNames.PAndLExHUD:
        return PLExHudAndPLHuDTable(
          "P&L (ex. HUD)",
          state.PLExHud,
          Number(year),
          handleChange,
          handleClose,
          0,
          inputsArrayIndex,
        );
      case inputGroupNames.PAndLHUD:
        return PLExHudAndPLHuDTable(
          "P&LHUD",
          state.PLHud,
          Number(year),
          handleChange,
          handleClose,
          1,
          inputsArrayIndex,
        );
      case inputGroupNames.Headcount:
        return HeadcountAndNoTitleTable(
          "Headcount",
          state.Headcount,
          Number(year),
          handleChange,
          handleClose,
          2,
          inputsArrayIndex,
        );
      case inputGroupNames.NoTitle:
        return HeadcountAndNoTitleTable(
          "NoTitle",
          state.NoTitle,
          Number(year),
          handleChange,
          handleClose,
          3,
          inputsArrayIndex,
        );
      case inputGroupNames.Portfolio:
        return PortfolioTable(
          "Portfolio",
          state.Portfolio,
          Number(year),
          handleChange,
          handleClose,
          6,
          inputsArrayIndex,
        );
      case inputGroupNames.WorkingCapital:
        return WorkingCapitalTable(
          "Working Capital",
          state.WorkingCapital,
          Number(year),
          handleChange,
          handleClose,
          7,
          inputsArrayIndex,
        );
      case inputGroupNames.Other:
        return OtherTable(
          "Other",
          state.Other,
          Number(year),
          handleChange,
          handleClose,
          8,
          inputsArrayIndex,
        );
      default:
        return <></>;
    }
  };

  const PlotlyRefArray = [
    useRef<Plot>(),
    useRef<Plot>(),
    useRef<Plot>(),
    useRef<Plot>(),
    useRef<Plot>(),
    useRef<Plot>(),
  ];

  return (
    <div className="module-master">
      <div style={{ display: "flex", alignItems: "center" }}>
        <div className="module-title">Financial Modeling</div>
      </div>
      <div
        className="module-row"
        style={{ display: state.errorFlag ? "block" : "none" }}
      >
        {state.errorFlag && (
          <Error
            code={state.errorCode}
            type={state.errorType}
            message={state.errorMessage}
          />
        )}
      </div>
      <div
        className="module-row"
        style={{ display: state.errorFlag ? "none" : "block" }}
      >
        {!state.initialDataLoadedFlag ? (
          <div className="loading-div">
            <FontAwesomeIcon
              icon={faSpinner}
              size="2x"
              spin={true}
              style={{ margin: "0 auto" }}
            />
          </div>
        ) : (
          <div>
            <div style={{ display: "flex" }}>
              <div className="bordered-object" style={{ display: "flex" }}>
                <div style={{ padding: "5px" }}>
                  Select Input
                  <Select
                    onChange={handleInputSelectionChange}
                    options={inputGroups}
                    className="basic-multi-select card-text small-selectors"
                    classNamePrefix="select"
                    closeMenuOnSelect={true}
                  />
                </div>
                <div style={{ padding: "5px" }}>
                  Select Year
                  <Select
                    onChange={handleYearSelectionChange}
                    options={yearGroups}
                    className="basic-multi-select card-text small-selectors"
                    classNamePrefix="select"
                    closeMenuOnSelect={true}
                  />
                </div>
              </div>
              <div
                className="bordered-object vertical-align"
                style={{ height: "auto" }}
              >
                <button
                  className="btn"
                  disabled={state.addInputsDisableFlag}
                  onClick={() => {
                    var newSelectedInputs = state.inputsArray;
                    let obj = newSelectedInputs.find((o, i) => {
                      if (
                        o.input === state.selectedInputTMP.value &&
                        o.year === Number(state.selectedYearTMP.value)
                      ) {
                        newSelectedInputs[i] = {
                          input: state.selectedInputTMP.value,
                          year: Number(state.selectedYearTMP.value),
                          enable: true,
                        };
                        return true;
                      }
                    });

                    setState({
                      ...state,
                      inputsArray: newSelectedInputs,
                      addInputsDisableFlag: true,
                    });
                  }}
                >
                  Add Inputs
                </button>
              </div>
              <div className="bordered-object" style={{ display: "flex" }}>
                <div style={{ padding: "5px" }}>
                  Select P&L KPIs
                  <Select
                    onChange={handlePLKPISelectionChange}
                    options={plKPIGroups}
                    className="basic-multi-select card-text small-selectors"
                    classNamePrefix="select"
                    closeMenuOnSelect={false}
                    isMulti={true}
                  />
                </div>
                <div style={{ padding: "5px" }}>
                  Select Balance Sheet KPIs
                  <Select
                    onChange={handleBSKPISelectionChange}
                    options={bsKPIGroups}
                    className="basic-multi-select card-text small-selectors"
                    classNamePrefix="select"
                    closeMenuOnSelect={false}
                    isMulti={true}
                  />
                </div>
              </div>
              <div
                className="bordered-object vertical-align"
                style={{ height: "auto" }}
              >
                <button
                  disabled={state.chartsLoadingFlag}
                  className="btn"
                  onClick={() => {
                    sendDataBuildCharts();
                  }}
                >
                  Run Model
                </button>
              </div>
              <div
                className="bordered-object vertical-align"
                style={{ height: "auto" }}
              >
                <Link
                  to="/Gordon_Financial_Model_vWeb_v2.xlsx"
                  target="_blank"
                  download
                >
                  Download Model
                </Link>
              </div>
            </div>
            <div className={`card my-2`}>
              <div className="card-body" style={{ padding: "5px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <button
                    className="btn me-2"
                    onClick={() =>
                      setState({
                        ...state,
                        openInputsMenu: !state.openInputsMenu,
                      })
                    }
                  >
                    {state.openInputsMenu ? (
                      <FontAwesomeIcon icon={faArrowUp} />
                    ) : (
                      <FontAwesomeIcon icon={faArrowDown} />
                    )}
                  </button>
                  <h5
                    className="card-title mb-0"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setState({
                        ...state,
                        openInputsMenu: !state.openInputsMenu,
                      })
                    }
                  >
                    Input Groups
                  </h5>
                </div>
                <div
                  className="flex-scroll-parent"
                  style={{
                    display: state.openInputsMenu ? "flex" : "none",
                    padding: "5px",
                  }}
                >
                  <div className="flex-scroll-child">
                    {state.inputsArray[0].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2024,
                        0,
                      )}
                    {state.inputsArray[1].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2025,
                        1,
                      )}
                    {state.inputsArray[2].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2026,
                        2,
                      )}
                    {state.inputsArray[3].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2027,
                        3,
                      )}
                    {state.inputsArray[4].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2028,
                        4,
                      )}
                    {state.inputsArray[5].enable &&
                      tableRenderingHandler(
                        inputGroupNames.PAndLExHUD,
                        2029,
                        5,
                      )}
                    {state.inputsArray[6].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2024, 6)}
                    {state.inputsArray[7].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2025, 7)}
                    {state.inputsArray[8].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2026, 8)}
                    {state.inputsArray[9].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2027, 9)}
                    {state.inputsArray[10].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2028, 10)}
                    {state.inputsArray[11].enable &&
                      tableRenderingHandler(inputGroupNames.PAndLHUD, 2029, 11)}
                    {state.inputsArray[12].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2024,
                        12,
                      )}
                    {state.inputsArray[13].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2025,
                        13,
                      )}
                    {state.inputsArray[14].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2026,
                        14,
                      )}
                    {state.inputsArray[15].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2027,
                        15,
                      )}
                    {state.inputsArray[16].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2028,
                        16,
                      )}
                    {state.inputsArray[17].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Headcount,
                        2029,
                        17,
                      )}
                    {state.inputsArray[18].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2024, 18)}
                    {state.inputsArray[19].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2025, 19)}
                    {state.inputsArray[20].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2026, 20)}
                    {state.inputsArray[21].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2027, 21)}
                    {state.inputsArray[22].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2028, 22)}
                    {state.inputsArray[23].enable &&
                      tableRenderingHandler(inputGroupNames.NoTitle, 2029, 23)}
                    {state.inputsArray[24].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2024,
                        24,
                      )}
                    {state.inputsArray[25].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2025,
                        25,
                      )}
                    {state.inputsArray[26].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2026,
                        26,
                      )}
                    {state.inputsArray[27].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2027,
                        27,
                      )}
                    {state.inputsArray[28].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2028,
                        28,
                      )}
                    {state.inputsArray[29].enable &&
                      tableRenderingHandler(
                        inputGroupNames.Portfolio,
                        2029,
                        29,
                      )}
                    {state.inputsArray[30].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2024,
                        30,
                      )}
                    {state.inputsArray[31].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2025,
                        31,
                      )}
                    {state.inputsArray[32].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2026,
                        32,
                      )}
                    {state.inputsArray[33].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2027,
                        33,
                      )}
                    {state.inputsArray[34].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2028,
                        34,
                      )}
                    {state.inputsArray[35].enable &&
                      tableRenderingHandler(
                        inputGroupNames.WorkingCapital,
                        2029,
                        35,
                      )}
                    {state.inputsArray[36].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2024, 36)}
                    {state.inputsArray[37].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2025, 37)}
                    {state.inputsArray[38].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2026, 38)}
                    {state.inputsArray[39].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2027, 39)}
                    {state.inputsArray[40].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2028, 40)}
                    {state.inputsArray[41].enable &&
                      tableRenderingHandler(inputGroupNames.Other, 2029, 41)}
                  </div>
                </div>
              </div>
            </div>
            {state.chartsLoadingFlag ? (
              <div className="loading-div">
                <FontAwesomeIcon
                  icon={faSpinner}
                  size="2x"
                  spin={true}
                  style={{ margin: "0 auto" }}
                />
              </div>
            ) : (
              <div>
                {state.chartsData.map((value) => {
                  return CelinkFinancialModelingKPI(
                    value,
                    xAxis,
                    value.metadata,
                    state.chartsLoadedFlag,
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
